import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import SEO from "../seo/SEO";

import TeamSection from "../components/About/TeamSection";
import ValuesSection from "../components/About/ValuesSection";
import HistorySection from "../components/About/HistorySection";
import CTA from "../components/CTA/CTA";

import "./AboutPage.css";


const AboutPage = () => {

  /* =========================================================
     STRUCTURED DATA
  ========================================================= */

  const aboutPageSchema = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",

      name: "About Bairagi Consultants",

      url:
        "https://www.bairagi-consultants.com/about",

      description:
        "Learn about Bairagi Consultants, a custom software development company building ecommerce platforms, mobile applications, CRM, MIS, dashboards, AI software and business automation solutions.",

      mainEntity: {
        "@type": "Organization",

        name: "Bairagi Consultants",

        url:
          "https://www.bairagi-consultants.com",

        logo:
          "https://www.bairagi-consultants.com/logo512.png",
      },
    },

    {
      "@context": "https://schema.org",
      "@type": "Organization",

      name: "Bairagi Consultants",

      url:
        "https://www.bairagi-consultants.com",

      logo:
        "https://www.bairagi-consultants.com/logo512.png",

      description:
        "Custom software development company providing ecommerce development, mobile application development, CRM, MIS, dashboards, AI solutions and business automation systems.",
    },
  ];


  return (
    <>

      {/* =====================================================
          SEO
      ====================================================== */}

      <SEO
        title="About Bairagi Consultants | Custom Software Development Company"
        description="Learn about Bairagi Consultants, our software development approach and expertise in ecommerce, mobile apps, CRM, MIS, dashboards, AI solutions and business automation."
        path="/about"
        schema={aboutPageSchema}
      />


      <div className="about-page">

        {/* =================================================
            HERO
        ================================================= */}

        <section className="about-hero">

          <div className="container">

            <motion.div
              className="about-hero-content"
              initial={{
                opacity: 0,
                y: 20,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.8,
              }}
            >

              <p className="about-label">
                ABOUT BAIRAGI CONSULTANTS
              </p>


              <h1>

                Custom Software

                <br />

                <span>
                  Built Around Business Needs
                </span>

              </h1>


              <p className="subtitle">

                We design and develop custom software,
                ecommerce platforms, mobile applications,
                CRM, MIS, dashboards, AI solutions and
                business automation systems for
                organizations looking to improve their
                digital operations.

              </p>

            </motion.div>

          </div>

        </section>


        {/* =================================================
            COMPANY OVERVIEW
        ================================================= */}

        <section className="company-overview">

          <div className="container">

            <div className="overview-grid">

              {/* CONTENT */}

              <motion.div
                className="overview-content"
                initial={{
                  opacity: 0,
                  x: -20,
                }}
                whileInView={{
                  opacity: 1,
                  x: 0,
                }}
                transition={{
                  duration: 0.6,
                }}
                viewport={{
                  once: true,
                }}
              >

                <span className="section-label">
                  WHO WE ARE
                </span>


                <h2>
                  Technology Solutions Focused
                  on Real Business Problems
                </h2>


                <p>

                  Bairagi Consultants develops
                  custom software systems for
                  businesses and organizations
                  requiring practical, scalable
                  and maintainable digital
                  solutions.

                </p>


                <p>

                  Our work includes ecommerce
                  platforms, mobile applications,
                  CRM systems, MIS solutions,
                  administration dashboards,
                  healthcare software, AI-enabled
                  applications and business
                  workflow automation.

                </p>


                <p>

                  Rather than forcing every
                  business into a predefined
                  software package, we focus on
                  understanding the workflow,
                  users, data and operational
                  requirements before designing
                  the solution.

                </p>


                <div className="overview-links">

                  <Link
                    to="/case-studies"
                    className="about-primary-link"
                  >
                    View Our Projects
                  </Link>


                  <Link
                    to="/contact"
                    className="about-secondary-link"
                  >
                    Talk To Us
                  </Link>

                </div>

              </motion.div>


              {/* IMAGE */}

              <motion.div
                className="overview-image"
                initial={{
                  opacity: 0,
                  x: 20,
                }}
                whileInView={{
                  opacity: 1,
                  x: 0,
                }}
                transition={{
                  duration: 0.6,
                }}
                viewport={{
                  once: true,
                }}
              >

                {/*
                  Replace this image later with an
                  actual company / software / team image.
                */}

                <div className="about-image-placeholder">

                  <div className="about-image-inner">

                    <span>
                      BAIRAGI CONSULTANTS
                    </span>

                    <h3>
                      Software • AI • Ecommerce
                    </h3>

                    <p>
                      Custom digital solutions
                      designed for business operations.
                    </p>

                  </div>

                </div>

              </motion.div>

            </div>

          </div>

        </section>


        {/* =================================================
            WHAT WE BUILD
        ================================================= */}

        <section className="about-expertise">

          <div className="container">

            <div className="about-section-heading">

              <span className="section-label">
                OUR EXPERTISE
              </span>


              <h2>
                Software Solutions We Build
              </h2>


              <p>

                Our development work spans
                customer-facing applications,
                internal management platforms,
                automation systems and
                data-driven business software.

              </p>

            </div>


            <div className="about-services-grid">

              <Link
                to="/ecommerce-development"
                className="about-service-card"
              >

                <span>01</span>

                <h3>
                  Ecommerce Development
                </h3>

                <p>
                  Online stores, ordering systems,
                  payments, catalogues and ecommerce
                  administration platforms.
                </p>

              </Link>


              <Link
                to="/mobile-app-development"
                className="about-service-card"
              >

                <span>02</span>

                <h3>
                  Mobile App Development
                </h3>

                <p>
                  Android and cross-platform mobile
                  applications connected with scalable
                  APIs and business systems.
                </p>

              </Link>


              <Link
                to="/crm-software-development"
                className="about-service-card"
              >

                <span>03</span>

                <h3>
                  CRM Software
                </h3>

                <p>
                  Custom customer management,
                  lead tracking, sales workflows
                  and business automation.
                </p>

              </Link>


              <Link
                to="/custom-dashboard-development"
                className="about-service-card"
              >

                <span>04</span>

                <h3>
                  MIS & Dashboards
                </h3>

                <p>
                  Management reporting, KPI monitoring,
                  analytics dashboards and operational
                  information systems.
                </p>

              </Link>


              <Link
                to="/ai-software-solutions"
                className="about-service-card"
              >

                <span>05</span>

                <h3>
                  AI Software Solutions
                </h3>

                <p>
                  AI assistants, document intelligence,
                  generative AI integration and
                  intelligent workflow automation.
                </p>

              </Link>


              <Link
                to="/case-studies"
                className="about-service-card"
              >

                <span>06</span>

                <h3>
                  Custom Business Software
                </h3>

                <p>
                  Purpose-built platforms designed
                  around specific operational and
                  organizational requirements.
                </p>

              </Link>

            </div>

          </div>

        </section>


        {/* =================================================
            DEVELOPMENT APPROACH
        ================================================= */}

        <section className="about-approach">

          <div className="container">

            <div className="about-section-heading">

              <span className="section-label">
                HOW WE WORK
              </span>


              <h2>
                From Business Requirement
                to Working Software
              </h2>

            </div>


            <div className="approach-grid">

              <div className="approach-card">

                <span>01</span>

                <h3>
                  Understand
                </h3>

                <p>
                  We first understand the business
                  problem, users, workflows and
                  expected outcomes.
                </p>

              </div>


              <div className="approach-card">

                <span>02</span>

                <h3>
                  Design
                </h3>

                <p>
                  We define the application flow,
                  architecture, database and
                  integration requirements.
                </p>

              </div>


              <div className="approach-card">

                <span>03</span>

                <h3>
                  Develop
                </h3>

                <p>
                  The software is implemented
                  using suitable frontend,
                  backend and database technologies.
                </p>

              </div>


              <div className="approach-card">

                <span>04</span>

                <h3>
                  Deploy & Improve
                </h3>

                <p>
                  Applications are deployed,
                  tested and improved based on
                  actual operational requirements.
                </p>

              </div>

            </div>

          </div>

        </section>


        {/* =================================================
            MISSION
        ================================================= */}

        <section className="mission-statement">

          <div className="container">

            <motion.div
              className="mission-content"
              initial={{
                opacity: 0,
              }}
              whileInView={{
                opacity: 1,
              }}
              transition={{
                duration: 0.8,
              }}
              viewport={{
                once: true,
              }}
            >

              <span className="section-label">
                OUR MISSION
              </span>


              <h2>
                Build Technology That
                Creates Business Value
              </h2>


              <blockquote>

                “To help businesses and organizations
                use custom software to simplify
                operations, improve efficiency and
                create better digital experiences.”

              </blockquote>

            </motion.div>

          </div>

        </section>


        {/* =================================================
            HISTORY
        ================================================= */}

        <HistorySection />


        {/* =================================================
            VALUES
        ================================================= */}

        <ValuesSection />


        {/* =================================================
            TEAM
        ================================================= */}

        {/* <TeamSection /> */}


        {/* =================================================
            CUSTOMER TESTIMONIALS
            TO BE ADDED LATER

            Suggested heading:
            "What Our Customers Think"

            Each testimonial:
            - Customer photo
            - Customer name
            - Company
            - Role
            - 2–4 line testimonial
        ================================================= */}


        {/* =================================================
            CTA
        ================================================= */}

        <CTA
          title="Have a software project in mind?"
          description="Tell us about your business requirement and let's explore the right software solution for it."
          buttonText="Book Free Consultation"
          buttonLink="/contact"
        />

      </div>

    </>
  );
};

export default AboutPage;